import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import RouteLoadingShell from "@/components/shared/RouteLoadingShell";
import {
  getPreferencesBootstrapDecision,
  preloadExistingPreferencesRoute,
  trackEvent,
} from "@/lib/api";
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import {
  resolveAuthenticatedSupabaseUser,
} from "@/lib/onboarding-auth";
import {
  getPreferencesSignInPath,
  resolvePreferencesDestinationPath,
} from "@/lib/preferences-resume";
import { storePreferencesEntryTelemetry } from "@/lib/preferences-entry-telemetry";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function preloadRouteForPath(pathname = "") {
  if (String(pathname || "").startsWith("/setup")) {
    return import("@/pages/Setup");
  }
  if (String(pathname || "").startsWith("/signin")) {
    return import("@/pages/SignIn");
  }
  if (String(pathname || "").startsWith("/link")) {
    return import("@/pages/Link");
  }
  return Promise.resolve();
}

export default function PreferencesEntry() {
  const navigate = useNavigate();
  const [lookupError, setLookupError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const startedAt = nowMs();
      try {
        const client = getSupabaseBrowserClient();
        if (!client) {
          if (!cancelled) {
            const nextPath = getPreferencesSignInPath();
            await preloadRouteForPath(nextPath);
            navigate(nextPath, { replace: true });
          }
          return;
        }

        const {
          data: { session },
          error: sessionError,
        } = await client.auth.getSession();
        if (sessionError) {
          throw sessionError;
        }

        const sessionUser = session?.user || null;
        if (!session?.access_token) {
          if (!cancelled) {
            const nextPath = getPreferencesSignInPath();
            await preloadRouteForPath(nextPath);
            navigate(nextPath, { replace: true });
          }
          return;
        }

        const user = await resolveAuthenticatedSupabaseUser(client, session);
        if (!user) {
          if (!cancelled) {
            const nextPath = getPreferencesSignInPath();
            await preloadRouteForPath(nextPath);
            navigate(nextPath, { replace: true });
          }
          return;
        }

        const gateStartedAt = nowMs();
        const entryDecision = await getPreferencesBootstrapDecision();
        const gateLookupDurationMs = Math.round(nowMs() - gateStartedAt);
        const destination = String(entryDecision?.destination || "").trim().toLowerCase();
        let resolvedAccountId = String(entryDecision?.accountId || "").trim();
        const nextPath = resolvePreferencesDestinationPath(destination, {
          forceLiveExistingPreferences: ONBOARDING_DESIGN_MODE,
          accountId: resolvedAccountId,
        });

        let accountFetchDurationMs = null;
        let billingFetchDurationMs = null;

        if (["preferences", "setup"].includes(destination)) {
          const [preloaded] = await Promise.all([
            preloadExistingPreferencesRoute(resolvedAccountId, {
              accountPayload: entryDecision?.accountPayload || null,
            }),
            preloadRouteForPath(nextPath),
          ]);
          resolvedAccountId = String(preloaded?.accountId || resolvedAccountId).trim();
          accountFetchDurationMs = preloaded?.accountFetchDurationMs ?? null;
          billingFetchDurationMs = preloaded?.billingFetchDurationMs ?? null;
        } else {
          await preloadRouteForPath(nextPath);
        }

        if (!cancelled) {
          const landingPath = resolvePreferencesDestinationPath(destination, {
            forceLiveExistingPreferences: ONBOARDING_DESIGN_MODE,
            accountId: resolvedAccountId,
          });
          const totalDurationMs = Math.round(nowMs() - startedAt);
          trackEvent("preferences_entry_resolved", {
            destination: destination || "unknown",
            total_duration_ms: totalDurationMs,
            gate_lookup_duration_ms: gateLookupDurationMs,
            server_app_duration_ms: entryDecision?.timing?.appDurationMs ?? null,
            server_auth_duration_ms: entryDecision?.timing?.authDurationMs ?? null,
            server_db_duration_ms: entryDecision?.timing?.lookupDurationMs ?? null,
            used_lightweight_entry: false,
            used_stored_session: Boolean(sessionUser),
            landing_path: landingPath,
          });
          if (["preferences", "setup"].includes(destination)) {
            storePreferencesEntryTelemetry({
              entryPath: "/p",
              startedAt,
              destination,
              accountFetchDurationMs,
              billingFetchDurationMs,
              gateLookupDurationMs,
              serverAppDurationMs: entryDecision?.timing?.appDurationMs ?? null,
              serverAuthDurationMs: entryDecision?.timing?.authDurationMs ?? null,
              serverDbDurationMs: entryDecision?.timing?.lookupDurationMs ?? null,
              usedSingleLoader: true,
            });
          }
          navigate(
            landingPath,
            { replace: true },
          );
        }
      } catch (error) {
        if (!cancelled) {
          setLookupError(error instanceof Error ? error.message : "Could not check your account.");
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (lookupError) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center px-5 text-center text-sm text-destructive">
        {lookupError}
      </div>
    );
  }

  return (
    <RouteLoadingShell
      title="Loading your account…"
    />
  );
}
